import WebHelpers from '~/app/webHelpers.js';
import UtilityAPI from '~/app/api/utilityAPI';

import * as Global from '../constants';
import { isSuperOrTechUserReadOnly } from '../Utility/general';

export default class CalcAPI {
  constructor(socketWorker, returnID, ddh, returnInitialized = false) {
    this.ddh = ddh;
    this.socketWorker = socketWorker;
    this.returnID = returnID;
    this.returnInitialized = returnInitialized;
    this.InitializeSocket();
  }

  // useful for external refund calculator
  switchReturnID = newID => {
    this.returnID = newID;
  };

  constructJSONMessage = (command, jsonObject) => {
    return JSON.stringify(
      Object.assign(jsonObject, {
        command,
        returnID: this.returnID,
      }),
    );
  };

  InitializeSocket = () => {
    this.socketWorker.postMessage(
      JSON.stringify({
        command: 'init',
        socketURL: WEBSOCKURL,
        accessToken: WebHelpers.getJWT(),
        drilldownToken: WebHelpers.getDrilldownToken(),
        returnID: this.returnID,
        returnInitialized: this.returnInitialized,
      }),
    );
  };

  CloseSession = () => {
    this.socketWorker.postMessage(
      JSON.stringify({
        command: 'session_close',
      }),
    );
  };

  CloseSocket = () => {
    this.socketWorker.postMessage(
      JSON.stringify({
        command: 'socket_close',
      }),
    );
  };

  Retry = () => {
    this.socketWorker.postMessage(
      JSON.stringify({
        command: 'retry',
      }),
    );
  };

  // Instead of opening a new websocket, we can reuse the same one and just swap the tokens.
  RefreshAndRetry = () => {
    this.socketWorker.postMessage(
      JSON.stringify({
        command: 'refresh_and_retry',
        accessToken: WebHelpers.getJWT(),
        drilldownToken: WebHelpers.getDrilldownToken(),
      }),
    );
  };

  SetReturnInitStatus = isReturnInitialized => {
    this.socketWorker.postMessage(
      JSON.stringify({
        command: 'set_return_init_status',
        returnInitialized: isReturnInitialized,
      }),
    );
  };

  ReturnInit = async (userKey, isEstimator = false, blob = null) => {
    let activeOfficeID = 0;
    const payload = WebHelpers.getJWTPayload();
    if (payload.hierarchy_type_id === Global.HIERARCHY_TYPE.PREPARER) {
      activeOfficeID = payload.preparer_info.active_office_id;
    } else {
      activeOfficeID = await UtilityAPI.getLastDrilledOfficeID(this.ddh);
    }

    let body = {
      login_id: userKey,
      role: payload.hierarchy_type_id,
      is_estimator: isEstimator,
      active_office_id: activeOfficeID,
    };
    if (blob !== null) {
      body = {
        ...body,
        blob,
      };
    }
    const message = this.constructJSONMessage('return_init', body);
    this.socketWorker.postMessage(message);
  };

  ReturnCalc = (formName, assetCalc, field, val, calcType) => {
    // Form name must be uppercase, however occurrance is case sensitive past 9 entries in return.
    const occuranceNumber = formName.substring(4, 6); // get occuranceNumber of form
    let adjustedFormName = formName.toUpperCase(); // Uppercase entire formname
    adjustedFormName =
      adjustedFormName.substring(0, 4) + occuranceNumber + adjustedFormName.substring(6); // add back orginal occuranceNumber

    const message = this.constructJSONMessage('calc', {
      formName: adjustedFormName,
      astCalc: assetCalc, // TODO: Temporary flag for calc on an asset form
      field,
      value: val,
      calcType,
    });
    this.socketWorker.postMessage(message);
  };

  RefundCalcCreateNewReturn = (rtnId, rfndCalcBlob, ssn, rtnBlob) => {
    const message = this.constructJSONMessage('refundCalculator_createReturn', {
      rtnId,
      rtnBlob,
      newSSN: ssn,
      rfndCalcBlob,
    });
    this.socketWorker.postMessage(message);
  };

  WizardCalc = returnFlds => {
    const message = this.constructJSONMessage('wizard_calc', {
      returnFlds, // return field is an array of fields with associated value
    });
    this.socketWorker.postMessage(message);
  };

  ReturnChoiceList = (formOccur, field, filter = '') => {
    field = field.substring(0, 4); // Calcserver only needs 4 character field name
    const message = this.constructJSONMessage('return_choicelist', {
      formOccur,
      field,
      filter,
      limit: filter !== '' ? 5 : null, // Limit 5 if  included filter
    });
    this.socketWorker.postMessage(message);
  };

  ReturnFieldLinkage = formOccur => {
    const message = this.constructJSONMessage('return_linkage', {
      formOccur,
    });
    this.socketWorker.postMessage(message);
  };

  ReturnFieldLinkPick = formOccur => {
    const message = this.constructJSONMessage('return_linkPick', {
      listPick: formOccur,
    });
    this.socketWorker.postMessage(message);
  };

  ReturnFormList = (pkg, readOnly = false) => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_formslist', {
        pkg,
        readOnly,
      }),
    );
  };

  ReturnListK1s = pkg => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_listk1s', {
        pkg,
      }),
    );
  };

  ReturnAddK1 = () => {
    this.socketWorker.postMessage(this.constructJSONMessage('return_addk1', {}));
  };

  ReturnDeleteK1 = formOccur => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_deletek1', {
        formOccur,
      }),
    );
  };

  ReturnEmailK1 = formOccur => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_emailk1', {
        formOccur,
      }),
    );
  };

  // Flag) 0 = show, 1 = do not show, see corpnav, :NAVLINE
  ReturnHideK1s = flag => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_hidek1s', {
        hideFlag: flag,
      }),
    );
  };

  ReturnNewAsset = (id, type, desc, date, assetCode) => {
    const calcDate = date.replace('/', '');
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_newasset', {
        dispID: id,
        astType: type,
        desc,
        date: calcDate,
        astCode: assetCode,
      }),
    );
  };

  ReturnViewAsset = id => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_viewasset', {
        astID: id,
      }),
    );
  };

  ReturnDeleteAsset = id => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_deleteasset', {
        astID: id,
      }),
    );
  };

  ReturnReassignAssetList = astID => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_reassignassetlist', {
        astID,
      }),
    );
  };

  ReturnReassignAsset = (astID, actvID) => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_reassignasset', {
        astID,
        actvID,
      }),
    );
  };

  ReturnRemoveDocument = docID => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_remove_document', {
        document_id: parseInt(docID),
      }),
    );
  };

  ReturnDetachDocument = (docID, attachVar) => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_document_detach', {
        document_id: parseInt(docID),
        document_status_var: attachVar,
      }),
    );
  };

  ReturnPDFAttach = (
    returnID,
    btnID,
    efcode,
    description,
    activeForm,
    document,
    documentName,
    documentDescription,
    documentType,
    attachmentType,
    documentID,
    interviewMode,
    binaryAttachment,
    dataBin,
    dataBinSig,
  ) => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_pdfAttach', {
        ...binaryAttachment,
        returnID: parseInt(returnID),
        btnid: btnID,
        efcode,
        userDesc: description,
        formOccur: activeForm,
        document,
        documentName,
        documentDescription,
        documentType: parseInt(documentType),
        attachmentType,
        documentID: parseInt(documentID),
        interviewMode,
        dataBin,
        dataBinSig,
      }),
    );
  };

  ReturnUploadCSV = (file, spouse) => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_stockImport', {
        file,
        spouse,
      }),
    );
  };

  ReturnSaveAndClose = (returnMetaData, isWizard = false) => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_saveclose', {
        returnMetaData,
        isWizard,
      }),
    );
  };

  ReturnSave = (returnMetaData, readOnly = false, isWizard = false) => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_save', {
        returnMetaData,
        readOnly,
        isWizard,
      }),
    );
  };

  TriggerAutoSave = (readOnly = false) => {
    this.socketWorker.postMessage(this.constructJSONMessage('return_auto_save', { readOnly }));
  };

  GetTextlinkMessageContent = (cltr = 'English') => {
    this.socketWorker.postMessage(this.constructJSONMessage('get_sms_message', { cltr }));
  };

  CrosslinkVersions = returnMetaData => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('crosslink_versions', {
        returnMetaData,
      }),
    );
  };

  ReturnClose = (readOnly = false) => {
    this.socketWorker.postMessage(this.constructJSONMessage('return_close', { readOnly }));
  };

  ReturnDelete = () => {
    this.socketWorker.postMessage(this.constructJSONMessage('return_delete', {}));
  };

  ReturnVerify = () => {
    const payload = WebHelpers.getJWTPayload();

    // grab readOnly flag from isSuperOrTechUserReadOnly() to match with what's being passed on return_init
    const readOnly = isSuperOrTechUserReadOnly(payload);
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_verify', {
        readOnly,
      }),
    );
  };

  ReturnFormLoad = (formOccur, stmType, file, field) => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('form_load', {
        formName: formOccur,
        stmType,
        formFile: file,
        field,
      }),
    );
  };

  ReturnAddForm = (formOccur, tors) => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_addform', {
        formOccur,
        tors,
      }),
    );
  };

  ReturnChoiceListCalc = (formOccur, field, value) => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_choicelist_calc', {
        formName: formOccur,
        field,
        value,
      }),
    );
  };

  ReturnRemoveForm = formOccur => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_removeform', {
        formOccur,
      }),
    );
  };

  ReturnReloadBilling = () => {
    this.socketWorker.postMessage(this.constructJSONMessage('return_bill_reload', {}));
  };

  SetReturnReviewStatus = flag => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('submit_for_review', {
        reviewStatus: flag,
      }),
    );
  };

  ReturnRemovePackage = pkg => {
    console.log('returnremovepackage called with: ' + pkg);
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_removepackage', {
        pkg,
      }),
    );
  };

  ReturnAddAsset = () => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_addasset', {
        // TODO: Send Data
      }),
    );
  };

  ReturnMessageResponse = (resp, formName, labelName, tvaID) => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_msgresp', {
        resp,
        formName,
        var: labelName,
        tvaID,
      }),
    );
  };

  ReturnPrint = (
    type,
    prefs,
    formOccur,
    sigs,
    pAction,
    password,
    cltr = 'English',
    isReadOnly,
    pkg = '',
    originalSignquery = '', // only used if signing object
    consent = '', // only used for signature docs with bank and/or 4547 form
  ) => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_print', {
        formOccur,
        pType: type,
        prefs,
        sigs,
        pAction,
        val: formOccur,
        pdfPwd: password,
        cltr,
        readOnly: isReadOnly,
        pkg,
        originalSignquery,
        consent,
      }),
    );
  };

  ReturnPrintStateClientLetter = (
    type,
    prefs,
    formOccur,
    sigs,
    pAction,
    password,
    cltr = 'English',
    val = '',
    isReadOnly,
  ) => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_print', {
        formOccur,
        pType: type,
        prefs,
        sigs,
        pAction,
        val,
        pdfPwd: password,
        cltr,
        readOnly: isReadOnly,
      }),
    );
  };

  ReturnPrintSignatures = (formOccur, type, LPA5, LPB5, readOnly = false, val) => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_print_query', {
        formOccur,
        pType: type,
        LPA5,
        LPB5,
        readOnly,
        val,
      }),
    );
  };

  ReturnStateAck = () => {
    this.socketWorker.postMessage(this.constructJSONMessage('return_stateacks', {}));
  };

  ReturnAmendedStateAck = () => {
    this.socketWorker.postMessage(this.constructJSONMessage('return_amended_stateacks', {}));
  };

  ReturnExtensionStateAck = () => {
    this.socketWorker.postMessage(this.constructJSONMessage('return_extension_stateacks', {}));
  };

  ReturnAttachedStates = () => {
    this.socketWorker.postMessage(this.constructJSONMessage('return_attachedstates', {}));
  };

  ReturnYearToYear = (formOccur, priorID) => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_yty', {
        formName: formOccur,
        priorReturnID: priorID,
      }),
    );
  };

  ReturnYearToYearOption = (formOccur, priorID, option) => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_yty', {
        formName: formOccur,
        priorReturnID: priorID,
        reloadOption: option,
      }),
    );
  };

  ReturnButtonPress = (
    buttonID,
    formOccur,
    dataFormName, // used server-side to verify the button signature
    dataCmd,
    dataSig,
    dataBin,
    dataBinSig,
  ) => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_buttonpress', {
        buttonID,
        formName: formOccur,
        dataFormName,
        dataCmd,
        dataSig,
        dataBin,
        dataBinSig,
      }),
    );
  };

  ReturnPaymentQuery = () => {
    this.socketWorker.postMessage(this.constructJSONMessage('return_payments_query', {}));
  };

  OutsideReturnPaymentQuery = async (userKey, blob = null) => {
    let activeOfficeID = 0;
    const payload = WebHelpers.getJWTPayload();
    if (payload.hierarchy_type_id === Global.HIERARCHY_TYPE.PREPARER) {
      activeOfficeID = payload.preparer_info.active_office_id;
    } else {
      activeOfficeID = await UtilityAPI.getLastDrilledOfficeID(this.ddh);
    }

    let body = {
      login_id: userKey,
      role: payload.hierarchy_type_id,
      active_office_id: activeOfficeID,
    };
    if (blob !== null) {
      body = {
        ...body,
        blob,
      };
    }
    const message = this.constructJSONMessage('outside_return_payments_query', body);
    this.socketWorker.postMessage(message);
  };

  CrosslinkVersions = async () => {
    this.socketWorker.postMessage(this.constructJSONMessage('crosslink_versions', {}));
  };

  PayJunctionTerminalStatus = paymentRequestID => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('payjunction_terminal_status', {
        paymentRequestID,
      }),
    );
  };

  ReturnProcessPaymentDetails = (
    dateRcvd,
    rcvdFrom,
    payMethod,
    amt,
    refNum,
    memo,
    formOccur,
    guid,
    readOnly = false,
  ) => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_payments', {
        dateRcvd,
        rcvdFrom,
        payMethod,
        amt,
        refNum,
        memo,
        formOccur,
        paymentGUID: guid,
        readOnly,
      }),
    );
  };

  OutsideReturnProcessPaymentDetails = async (
    dateRcvd,
    rcvdFrom,
    payMethod,
    amt,
    refNum,
    memo,
    formOccur,
    guid,
    userKey,
  ) => {
    let activeOfficeID = 0;
    const payload = WebHelpers.getJWTPayload();
    if (payload.hierarchy_type_id === Global.HIERARCHY_TYPE.PREPARER) {
      activeOfficeID = payload.preparer_info.active_office_id;
    } else {
      activeOfficeID = await UtilityAPI.getLastDrilledOfficeID(this.ddh);
    }

    this.socketWorker.postMessage(
      this.constructJSONMessage('outside_return_payments', {
        dateRcvd,
        rcvdFrom,
        payMethod,
        amt,
        refNum,
        memo,
        formOccur,
        paymentGUID: guid,
        login_id: userKey,
        role: payload.hierarchy_type_id,
        active_office_id: activeOfficeID,
      }),
    );
  };

  ReturnProfile = () => {
    this.socketWorker.postMessage(this.constructJSONMessage('return_profile', {}));
  };

  // Not a catchall for all errors and is very specific to just invalid json being sent from calc, but this can be later expanded on as needed.
  ReturnRaiseError = (badmsg, errorMessage, errorStack) => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_raise_error', {
        invalidJson: badmsg,
        errorMessage,
        errorStack,
      }),
    );
  };

  ReturnBookmarkQuery = (formOccur, fieldID) => {
    if (fieldID.length > 4) {
      fieldID = fieldID.substring(0, 4);
    }
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_bookmark_query', {
        formOccur,
        fieldID,
      }),
    );
  };

  ReturnBookmarkUpsert = (formOccur, fieldID, desc, timestamp, deleteBookmark) => {
    if (fieldID.length > 4) {
      fieldID = fieldID.substring(0, 4);
    }
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_bookmark_upsert', {
        formOccur,
        fieldID,
        desc,
        timestamp,
        deleteBookmark,
      }),
    );
  };

  ReturnNotesUpsert = (note, deleteNote = false) => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_notes_upsert', {
        id: note.id.toString(),
        timestamp: note.timestamp,
        note: note.note,
        owner: note.owner,
        deleteNote,
      }),
    );
  };

  ReturnToggleStatusLock = () => {
    this.socketWorker.postMessage(this.constructJSONMessage('return_toggle_status_lock', {}));
  };

  CopyToTraining = () => {
    this.socketWorker.postMessage(this.constructJSONMessage('return_copy_to_training', {}));
  };

  CopyToTrainingDupeCheck = () => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_copy_to_training_dupe_check', {}),
    );
  };

  SendRemotePayment = () => {
    this.socketWorker.postMessage(this.constructJSONMessage('return_rmt_pay', {}));
  };

  SendOutsideRemotePayment = () => {
    this.socketWorker.postMessage(this.constructJSONMessage('outside_return_rmt_pay', {}));
  };

  CopyToLive = () => {
    this.socketWorker.postMessage(this.constructJSONMessage('return_copy_to_live', {}));
  };

  CopyToLiveDupeCheck = () => {
    this.socketWorker.postMessage(this.constructJSONMessage('return_copy_to_live_dupe_check', {}));
  };

  SendTextLinkMessage = msg => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('send_textlink_message', {
        name: msg.name,
        cellNumber: msg.number,
        cellCarrier: msg.carrier,
        message: msg.message,
        returnID: msg.returnID,
        carrierDomain: msg.carrierDomain,
      }),
    );
  };

  UpdateRestrictedFieldStatus = (row, formOccur) => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_restricted_field', {
        field: row.field,
        description: row.description,
        formID: row.formID,
        formTitle: row.formTitle,
        status: row.status,
        formOccur,
      }),
    );
  };

  TVASend = (imageBlob, imageName, imageDescription, imageType, isPDF) => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_tvasend', {
        imageBlob,
        imageName,
        imageDescription,
        imageType,
        isPDF,
      }),
    );
  };

  TVARetreive = tvaID => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_tvaretrieve', {
        tvaID,
      }),
    );
  };

  TVAUpdate = tvaID => {
    this.socketWorker.postMessage(
      this.constructJSONMessage('return_tvaupdate', {
        tvaID,
      }),
    );
  };

  TVAHelp = formOccur => {
    this.socketWorker.postMessage(this.constructJSONMessage('return_tvahelp', { formOccur }));
  };
}
